import React, { useEffect, useMemo, useState } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import {
    Users,
    TrendingUp,
    MapPin,
    Filter,
    Download,
    CheckCircle,
    Award,
    Activity,
    Star,
    UserCheck,
    TrendingDown,
    LogOut,
    MessageSquareText,
    RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Calendar, BookOpen } from 'lucide-react';
import MultiSelectDropdown from './MultiSelectDropdown';
import apiClient from '../utils/api';
import { MEDashboardMetrics } from '../types';
import { workshops as workshopOptionsMaster, districts as districtNames, getImplementationPartners, getDepartments, getWorkshopName, getWorkshopTarget } from '../utils/analytics';
import LoadingOverlay from './LoadingOverlay';
import { PortalLogoLink } from './PortalLogoLink';
import { useCache } from '../contexts/CacheContext';

interface RampMEDashboardProps {
    onLogout: () => void;
}

const CHART_COLORS = [
    '#0D52BD',
    '#10b981',
    '#60a5fa',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#14b8a6',
    '#ec4899'
];

const normalizeGenderName = (name: string) => {
    const n = (name || '').trim();
    // Fix known typo coming from older payloads/source data
    if (n.toLowerCase() === 'transgende') return 'Transgender';
    return n;
};

const getGenderColor = (name: string | undefined, index: number) => {
    const normalized = normalizeGenderName(name || '').toLowerCase();
    if (normalized === 'male') return '#0D52BD';
    if (normalized === 'female') return '#ec4899';
    if (normalized === 'transgender') return '#14b8a6'; // distinct color for Transgender
    return CHART_COLORS[index % CHART_COLORS.length];
};

// Enhanced Glassmorphic Tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="backdrop-blur-xl bg-white/80 p-4 rounded-2xl shadow-2xl border border-white/20 transform transition-all duration-300">
                <p className="font-bold text-slate-800 mb-2 text-sm">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 mb-1">
                        <div
                            className="w-3 h-3 rounded-full animate-pulse"
                            style={{ backgroundColor: entry.color }}
                        ></div>
                        <p className="text-sm font-medium text-slate-700">
                            {`${entry.name}: `}
                            <span className="font-bold">
                                {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
                            </span>
                        </p>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};


// Recharts Pie label renderer - positions labels outside the donut with connecting lines
const renderPiePercentLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    name
}: any) => {
    // Show all labels regardless of percentage (except zero)
    if (percent === 0) return null;

    // Calculate position outside the outer radius
    const RADIAN = Math.PI / 180;
    // Position labels further out to ensure visibility
    const labelRadius = outerRadius + 30; // Position 30px outside the outer radius
    const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
    const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);

    // Calculate line end point (on the outer edge of the donut)
    const lineEndX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const lineEndY = cy + outerRadius * Math.sin(-midAngle * RADIAN);

    // Determine text anchor based on angle to prevent overlap
    const isRightSide = x > cx;
    const textAnchor = isRightSide ? 'start' : 'end';

    // Add offset for better visibility
    const offsetX = isRightSide ? 8 : -8;

    return (
        <g>
            {/* Connecting line from donut to label */}
            <line
                x1={lineEndX}
                y1={lineEndY}
                x2={x}
                y2={y}
                stroke="#94a3b8"
                strokeWidth={1}
                style={{ pointerEvents: 'none' }}
            />
            {/* Label text */}
            <text
                x={x + offsetX}
                y={y}
                fill="#1e293b"
                textAnchor={textAnchor}
                dominantBaseline="central"
                fontSize={12}
                fontWeight={700}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        </g>
    );
};

const RampMEDashboard: React.FC<RampMEDashboardProps> = ({ onLogout }) => {
    const navigate = useNavigate();
    const { lastInvalidated, refreshDashboard } = useCache();
    const [metrics, setMetrics] = useState<MEDashboardMetrics | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const AGE_DISTRIBUTION_HIDDEN_WORKSHOP_IDS = useMemo(() => new Set(['10', '11', '12']), []);

    // Workshop End Date filter must not allow dates before Oct 1, 2025
    const MIN_WORKSHOP_END_DATE = '2025-10-01';
    const maxDate = (a: string, b: string) => (a >= b ? a : b); // safe for YYYY-MM-DD strings
    const clampMinDate = (v: string, min: string) => {
        if (!v) return '';
        return v < min ? min : v;
    };

    const defaultFilters = () => ({
        workshops: [] as string[],
        districts: [] as string[],
        implementingAgencies: [] as string[],
        startDate: '',
        endDate: ''
    });

    const [pendingFilters, setPendingFilters] = useState(defaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

    const normalizeDateFilters = (filters: ReturnType<typeof defaultFilters>) => {
        // No restriction for "from" date; only restrict "to" date (>= Oct 1, 2025)
        const start = filters.startDate || '';
        const endMin = start ? maxDate(MIN_WORKSHOP_END_DATE, start) : MIN_WORKSHOP_END_DATE;
        const end = clampMinDate(filters.endDate, endMin);
        return { ...filters, startDate: start, endDate: end };
    };

    const hasPendingChanges = useMemo(() => {
        return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
    }, [pendingFilters, appliedFilters]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const m = await apiClient.getMEDashboardMetrics({
                    startDate: appliedFilters.startDate || undefined,
                    endDate: appliedFilters.endDate || undefined,
                    workshopId: appliedFilters.workshops.length ? appliedFilters.workshops.join(',') : undefined,
                    district: appliedFilters.districts.length ? appliedFilters.districts.join(',') : undefined,
                    implementationPartnerId: appliedFilters.implementingAgencies.length ? appliedFilters.implementingAgencies.join(',') : undefined,
                });
                if (!cancelled) setMetrics(m);
            } catch {
                if (!cancelled) setMetrics(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [appliedFilters, lastInvalidated]); // Add lastInvalidated as dependency

    const exportReport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (appliedFilters.startDate) params.set('startDate', appliedFilters.startDate);
            if (appliedFilters.endDate) params.set('endDate', appliedFilters.endDate);
            if (appliedFilters.workshops.length) params.set('workshopId', appliedFilters.workshops.join(','));
            if (appliedFilters.districts.length) params.set('district', appliedFilters.districts.join(','));
            if (appliedFilters.implementingAgencies.length) params.set('implementationPartnerId', appliedFilters.implementingAgencies.join(','));

            const endpoint = `/api/dashboard/me/export${params.toString() ? `?${params.toString()}` : ''}`;
            const { blob, filename } = await apiClient.downloadCsv(endpoint);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'ramp_me_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            // Keep it simple: log and allow user to retry
            console.error('Failed to export M&E report', e);
            alert('Failed to export report. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const kpis = useMemo(() => metrics?.kpis || ({
        totalWorkshops: 0,
        totalParticipants: 0,
        avgParticipants: 0,
        minorityParticipants: 0,
        avgAge: 0,
        avgRating: 0,
    }), [metrics]);

    const monthlyTrend = useMemo(() => metrics?.monthlyTrend || [], [metrics]);

    const districtBreakdown = useMemo(() => metrics?.districtBreakdown || ({ districts: [] }), [metrics]);

    const ageGroupData = useMemo(() => metrics?.ageDistribution || [], [metrics]);

    const knownAgeGroupData = useMemo(() => {
        const isUnknown = (v: any) => String(v?.name ?? '').trim().toLowerCase() === 'unknown';
        return ageGroupData
            .filter((x: any) => !isUnknown(x))
            .filter((x: any) => (Number(x?.value) || 0) > 0);
    }, [ageGroupData]);

    const hideAgeDistribution = useMemo(() => {
        // Requirement: if workshop_id is 10/11/12, don't show Age Distribution in the dashboard.
        if (appliedFilters.workshops.length !== 1) return false;
        return AGE_DISTRIBUTION_HIDDEN_WORKSHOP_IDS.has(appliedFilters.workshops[0]);
    }, [appliedFilters.workshops, AGE_DISTRIBUTION_HIDDEN_WORKSHOP_IDS]);

    const topSectors = useMemo(() => {
        const sectors = metrics?.topSectors || [];
        const isUnknown = (v: any) => String(v?.sector ?? '').trim().toLowerCase() === 'unknown';
        const nonUnknown = sectors.filter(s => !isUnknown(s));
        const top12 = nonUnknown.slice(0, 12);
        return { top12 };
    }, [metrics]);

    const demographicsData = useMemo(() => {
        const d = metrics?.demographics || ({ gender: [], category: [] });
        return {
            ...d,
            gender: (d.gender || []).map(g => ({ ...g, name: normalizeGenderName(g.name) })),
        };
    }, [metrics]);

    const workshopOptions = useMemo(() => workshopOptionsMaster, []);
    const districtOptions = useMemo(() => districtNames.map(d => ({ id: d, name: d })), []);
    const implementingAgencyOptions = useMemo(
        () => [...getImplementationPartners(), ...getDepartments()],
        []
    );

    // Helper to check if filters are set
    const hasFiltersSet = useMemo(() => {
        return !!(
            appliedFilters.startDate ||
            appliedFilters.endDate ||
            appliedFilters.workshops.length > 0 ||
            appliedFilters.districts.length > 0 ||
            appliedFilters.implementingAgencies.length > 0
        );
    }, [appliedFilters]);

    // Helper to check if a value should be hidden (null or zero)
    const shouldHideValue = (value: any): boolean => {
        if (value === null || value === undefined) return true;
        if (typeof value === 'number') return value === 0;
        if (typeof value === 'string') {
            const num = Number(value);
            return isNaN(num) || num === 0;
        }
        return false;
    };

    // Filter KPI tiles to only show non-zero/non-null values
    // Always hide avgAge and avgRating when they are 0, regardless of filters
    const visibleKpiTiles = useMemo(() => {
        const allTiles = [
            { key: 'totalWorkshops', value: kpis.totalWorkshops, label: 'Total Workshops', icon: CheckCircle, badge: loading ? 'Loading' : 'Completed' },
            { key: 'totalParticipants', value: kpis.totalParticipants, label: 'Total Participants', icon: Users, badge: 'Benefited' },
            { key: 'avgParticipants', value: kpis.avgParticipants, label: 'Avg Participants per workshop(batch)', icon: TrendingUp, badge: 'Avg' },
            { key: 'minorityParticipants', value: kpis.minorityParticipants, label: 'Minority Participants', icon: Users, badge: 'Inclusion' },
            { key: 'avgAge', value: kpis.avgAge, label: 'Avg Age', icon: UserCheck, badge: 'Years' },
            { key: 'avgRating', value: kpis.avgRating, label: 'Avg Overall Rating', icon: Star, badge: 'Rating', isRating: true },
        ];

        if (!hasFiltersSet) {
            // When no filters are set, hide avgAge and avgRating if they're 0, but show all others
            return allTiles.filter(tile => {
                if (tile.key === 'avgAge' || tile.key === 'avgRating') {
                    return !shouldHideValue(tile.value);
                }
                return true;
            });
        }

        // When filters are set, only show tiles with non-zero/non-null values
        return allTiles.filter(tile => !shouldHideValue(tile.value));
    }, [kpis, hasFiltersSet, loading]);

    // Check if charts should be hidden - always check for non-zero values
    const hasCategoryData = useMemo(() => {
        const category = demographicsData.category || [];
        return category.length > 0 && category.some((c: any) => (Number(c.value) || 0) > 0);
    }, [demographicsData.category]);

    const hasGenderData = useMemo(() => {
        const gender = demographicsData.gender || [];
        return gender.length > 0 && gender.some((g: any) => (Number(g.value) || 0) > 0);
    }, [demographicsData.gender]);

    const hasAgeData = useMemo(() => {
        if (hideAgeDistribution) return false;
        return knownAgeGroupData.length > 0 && knownAgeGroupData.some((a: any) => (Number(a.value) || 0) > 0);
    }, [knownAgeGroupData, hideAgeDistribution]);

    const hasMonthlyTrendData = useMemo(() => {
        return monthlyTrend.length > 0 && monthlyTrend.some((m: any) =>
            (Number(m.workshops) || 0) > 0 || (Number(m.participants) || 0) > 0
        );
    }, [monthlyTrend]);

    const hasSectorData = useMemo(() => {
        return topSectors.top12.length > 0 && topSectors.top12.some((s: any) => (Number(s.participants) || 0) > 0);
    }, [topSectors.top12]);

    const hasDistrictData = useMemo(() => {
        const districts = districtBreakdown.districts || [];
        return districts.length > 0 && districts.some((d: any) =>
            (Number(d.workshops) || 0) > 0 || (Number(d.participants) || 0) > 0
        );
    }, [districtBreakdown]);

    // Check if all data sections are hidden
    const allDataHidden = useMemo(() => {
        return visibleKpiTiles.length === 0 &&
            !hasCategoryData &&
            !hasGenderData &&
            !hasAgeData &&
            !hasMonthlyTrendData &&
            !hasSectorData &&
            !hasDistrictData;
    }, [visibleKpiTiles.length, hasCategoryData, hasGenderData, hasAgeData, hasMonthlyTrendData, hasSectorData, hasDistrictData]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 font-sans transition-colors duration-500" aria-busy={loading}>
            <LoadingOverlay show={loading} label="Loading M&E dashboard…" />
            {/* Animated Background Elements */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                <div className="absolute top-0 -right-4 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-8 left-20 w-96 h-96 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            {/* Main Content */}
            <div className="relative z-10">
                {/* Header with Glassmorphism - REMOVED GLOW */}
                <header className="mb-8">
                    <div className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-8 border border-white/20 transition-all duration-500 hover:shadow-blue-500/20 hover:shadow-3xl hover:scale-[1.01] group">
                        <div className="flex items-center justify-between flex-wrap gap-6">
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-3">
                                    <PortalLogoLink />
                                </div>
                                <div>
                                    <h1 className="text-4xl font-black bg-gradient-to-r from-[#0D52BD] to-blue-700 bg-clip-text text-transparent mb-2">
                                        RAMP M&E Dashboard
                                    </h1>
                                    <p className="text-slate-600 text-sm flex items-center gap-2"></p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button
                                    onClick={() => navigate('/feedback-dashboard')}
                                    className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-green-700 text-white px-6 py-3 rounded-xl hover:from-emerald-700 hover:to-green-800 transition-all shadow-lg hover:shadow-2xl hover:shadow-emerald-500/30 hover:scale-105 font-semibold group border border-white/20"
                                >
                                    <MessageSquareText size={20} />
                                    Feedback Dashboard
                                </button>
                                <button
                                    onClick={exportReport}
                                    disabled={exporting}
                                    className="flex items-center gap-2 bg-gradient-to-r from-[#0D52BD] to-blue-700 text-white px-6 py-3 rounded-xl hover:from-[#0a3d8f] hover:to-blue-800 transition-all shadow-lg hover:shadow-2xl hover:shadow-blue-500/30 hover:scale-105 font-semibold group border border-white/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <Download size={20} className={exporting ? '' : 'group-hover:animate-bounce'} />
                                    {exporting ? 'Exporting…' : 'Export Report'}
                                </button>
                                <button
                                    onClick={refreshDashboard}
                                    disabled={loading}
                                    className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all shadow-lg hover:shadow-2xl hover:shadow-indigo-500/30 hover:scale-105 font-semibold group border border-white/20 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                                    {loading ? 'Refreshing...' : 'Refresh Data'}
                                </button>
                                <button
                                    onClick={onLogout}
                                    className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-2xl hover:shadow-red-500/30 hover:scale-105 font-semibold group border border-white/20"
                                >
                                    <LogOut size={20} />
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Filters */}
                <section className="mb-8">
                    <div className="bg-white rounded-xl shadow-md p-5 border border-slate-200 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Filter size={20} className="text-slate-600" />
                                <h2 className="text-lg font-semibold text-slate-700">Filters</h2>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Date Range Filter */}
                            <div className="space-y-2">
                                <label className="flex text-sm font-medium text-slate-700 items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date Range (Workshop End Date)
                                </label>
                                <div className="space-y-2">
                                    <input
                                        type="date"
                                        value={pendingFilters.startDate}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            setPendingFilters(prev => {
                                                const nextStart = raw;
                                                const endMin = nextStart ? maxDate(MIN_WORKSHOP_END_DATE, nextStart) : MIN_WORKSHOP_END_DATE;
                                                const nextEnd = clampMinDate(prev.endDate, endMin);
                                                return { ...prev, startDate: nextStart, endDate: nextEnd };
                                            });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                                        placeholder="Start Date"
                                    />
                                    <input
                                        type="date"
                                        min={pendingFilters.startDate ? maxDate(MIN_WORKSHOP_END_DATE, pendingFilters.startDate) : MIN_WORKSHOP_END_DATE}
                                        value={pendingFilters.endDate}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            setPendingFilters(prev => {
                                                const start = prev.startDate || '';
                                                const endMin = start ? maxDate(MIN_WORKSHOP_END_DATE, start) : MIN_WORKSHOP_END_DATE;
                                                const end = clampMinDate(raw, endMin);
                                                return { ...prev, startDate: start, endDate: end };
                                            });
                                        }}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                                        placeholder="End Date"
                                    />
                                </div>
                            </div>

                            {/* Workshops (Components) */}
                            <MultiSelectDropdown
                                label="Workshops"
                                icon={<BookOpen className="w-4 h-4" />}
                                options={workshopOptions}
                                selectedValues={pendingFilters.workshops}
                                onSelectionChange={(selectedValues) =>
                                    setPendingFilters({ ...pendingFilters, workshops: selectedValues })
                                }
                                placeholder="Select workshops..."
                            />

                            {/* Districts */}
                            <MultiSelectDropdown
                                label="Districts"
                                icon={<MapPin className="w-4 h-4" />}
                                options={districtOptions}
                                selectedValues={pendingFilters.districts}
                                onSelectionChange={(selectedValues) =>
                                    setPendingFilters({ ...pendingFilters, districts: selectedValues })
                                }
                                placeholder="Select districts..."
                            />

                            {/* Implementing Agencies */}
                            <MultiSelectDropdown
                                label="Implementing Agencies"
                                icon={<Users className="w-4 h-4" />}
                                options={implementingAgencyOptions}
                                selectedValues={pendingFilters.implementingAgencies}
                                onSelectionChange={(selectedValues) =>
                                    setPendingFilters({ ...pendingFilters, implementingAgencies: selectedValues })
                                }
                                placeholder="Select agencies..."
                            />
                        </div>

                        <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-end">
                            <button
                                onClick={() => {
                                    const next = defaultFilters();
                                    setPendingFilters(next);
                                    setAppliedFilters(next);
                                }}
                                className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-semibold"
                            >
                                Reset Filters
                            </button>
                            <button
                                onClick={() => setAppliedFilters(normalizeDateFilters(pendingFilters))}
                                disabled={!hasPendingChanges}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#0D52BD] to-[#0a3d8f] text-white hover:from-[#0a3d8f] hover:to-blue-900 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold shadow-sm hover:shadow-md"
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </section>

                {/* KPI cards (DB-backed) */}
                {allDataHidden ? (
                    <section className="mb-6">
                        <div className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-12 border border-white/20 text-center">
                            <div className="flex flex-col items-center justify-center gap-4">
                                <div className="p-4 bg-slate-100 rounded-full">
                                    <Filter size={48} className="text-slate-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-700">No Data Available</h3>
                                <p className="text-slate-500 text-lg">There is no data available for the selected filters. Please try adjusting your filters.</p>
                            </div>
                        </div>
                    </section>
                ) : visibleKpiTiles.length > 0 ? (
                    <section className="mb-6 w-full">
                        <div className={`grid gap-4 w-full ${visibleKpiTiles.length === 1
                            ? 'grid-cols-1'
                            : visibleKpiTiles.length === 2
                                ? 'grid-cols-1 sm:grid-cols-2'
                                : visibleKpiTiles.length === 3
                                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                                    : visibleKpiTiles.length === 4
                                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                                        : visibleKpiTiles.length === 5
                                            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5'
                                            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6'
                            }`}>
                            {visibleKpiTiles.map((tile) => {
                                const IconComponent = tile.icon;
                                return (
                                    <div key={tile.key} className="bg-[#0D52BD] text-white rounded-xl shadow-lg p-5 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 w-full">
                                        <div className="flex items-start justify-between">
                                            <div className="p-3 bg-white/10 rounded-full">
                                                <IconComponent size={24} />
                                            </div>
                                            <span className="text-xs font-medium bg-white/10 text-white/80 px-2 py-1 rounded-full">
                                                {tile.badge}
                                            </span>
                                        </div>
                                        <p className="text-3xl font-bold mt-3 mb-1">
                                            {tile.isRating ? (
                                                <>
                                                    {Number(tile.value || 0).toFixed(2)} <span className="text-lg opacity-70">/ 5</span>
                                                </>
                                            ) : (
                                                typeof tile.value === 'number'
                                                    ? tile.value.toLocaleString()
                                                    : tile.value
                                            )}
                                        </p>
                                        <p className="text-sm text-white/80">{tile.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ) : null}

                {/* Workshop Progress Tiles */}
                {!hasFiltersSet && (() => {
                    const componentBreakdown = metrics?.componentBreakdown || [];
                    const workshopTiles = componentBreakdown
                        .filter(item => getWorkshopTarget(item.workshopId) !== null)
                        .map(item => {
                            const targetInfo = getWorkshopTarget(item.workshopId)!;
                            const current = targetInfo.type === 'participants' ? item.participants : item.workshops;
                            const target = targetInfo.target;
                            const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;

                            return {
                                workshopId: item.workshopId,
                                workshopName: getWorkshopName(item.workshopId),
                                current,
                                target,
                                type: targetInfo.type,
                                percentage,
                            };
                        })
                        .filter(tile => tile.current > 0) // Hide tiles where current value is 0
                        .sort((a, b) => a.workshopId.localeCompare(b.workshopId));

                    if (workshopTiles.length === 0) return null;

                    return (
                        <section className="mb-8">
                            <div className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-8 border border-white/20 transition-all duration-500 hover:shadow-blue-500/20 hover:shadow-3xl group">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-[#0D52BD] to-blue-700 bg-clip-text text-transparent">
                                    <div className="p-2 bg-gradient-to-br from-[#0D52BD] to-blue-700 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                        <CheckCircle size={22} className="text-white" />
                                    </div>
                                    Workshop Progress vs Targets
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {workshopTiles.map((tile) => (
                                        <div
                                            key={tile.workshopId}
                                            className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-6 border border-slate-200 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                                        >
                                            <h4 className="text-lg font-bold text-[#0D52BD] mb-4">{tile.workshopName}</h4>
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-semibold text-slate-600">
                                                        {tile.type === 'participants' ? 'Participants' : 'Workshops'}:
                                                    </span>
                                                    <span className="text-lg font-bold text-slate-900">
                                                        {tile.current.toLocaleString()} / {tile.target.toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-500 ${tile.percentage >= 50
                                                            ? 'bg-green-800'
                                                            : 'bg-green-700'
                                                            }`}
                                                        style={{ width: `${tile.percentage}%` }}
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium text-slate-500">Progress</span>
                                                    <span className="text-sm font-bold text-slate-700">
                                                        {tile.percentage.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    );
                })()}

                {/* REORDERED: Donut Charts RIGHT BELOW FILTERS */}
                {(() => {
                    const visibleCharts = [
                        hasCategoryData && {
                            key: 'category', component: (
                                <div key="category" className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-6 border border-white/20 transition-all duration-500 hover:shadow-blue-500/20 hover:shadow-3xl hover:scale-[1.02] group">
                                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2 bg-gradient-to-r from-[#0D52BD] to-blue-700 bg-clip-text text-transparent">
                                        <div className="p-2 bg-gradient-to-br from-[#0D52BD] to-blue-700 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                            <Award size={18} className="text-white" />
                                        </div>
                                        Social Category
                                    </h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                                            <Pie
                                                data={demographicsData.category}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={85}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={renderPiePercentLabel}
                                                labelLine={false}
                                                animationDuration={1500}
                                            >
                                                {demographicsData.category.map((entry: any, index: number) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                                                        stroke="#fff"
                                                        strokeWidth={3}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend wrapperStyle={{ fontSize: '13px', fontWeight: '600' }} iconType="circle" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )
                        },
                        hasAgeData && {
                            key: 'age', component: (
                                <div key="age" className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-6 border border-white/20 transition-all duration-500 hover:shadow-indigo-500/20 hover:shadow-3xl hover:scale-[1.02] group">
                                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 bg-clip-text text-transparent">
                                        <div className="p-2 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                            <Users size={18} className="text-white" />
                                        </div>
                                        Age Distribution
                                    </h3>
                                    {knownAgeGroupData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                                                <Pie
                                                    data={knownAgeGroupData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={55}
                                                    outerRadius={85}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                    label={renderPiePercentLabel}
                                                    labelLine={false}
                                                    animationDuration={1500}
                                                >
                                                    {knownAgeGroupData.map((entry: any, index: number) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={CHART_COLORS[(index + 4) % CHART_COLORS.length]}
                                                            stroke="#fff"
                                                            strokeWidth={3}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Legend wrapperStyle={{ fontSize: '13px', fontWeight: '600' }} iconType="circle" />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-[260px] flex items-center justify-center text-sm font-semibold text-slate-500">
                                            No filled age data
                                        </div>
                                    )}
                                </div>
                            )
                        },
                        hasGenderData && {
                            key: 'gender', component: (
                                <div key="gender" className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-6 border border-white/20 transition-all duration-500 hover:shadow-pink-500/20 hover:shadow-3xl hover:scale-[1.02] group">
                                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2 bg-gradient-to-r from-pink-600 to-pink-700 bg-clip-text text-transparent">
                                        <div className="p-2 bg-gradient-to-br from-pink-600 to-pink-700 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                            <Users size={18} className="text-white" />
                                        </div>
                                        Gender Distribution
                                    </h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                                            <Pie
                                                data={demographicsData.gender}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={85}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={renderPiePercentLabel}
                                                labelLine={false}
                                                animationDuration={1500}
                                            >
                                                {demographicsData.gender.map((entry: any, index: number) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={getGenderColor(entry?.name, index)}
                                                        stroke="#fff"
                                                        strokeWidth={3}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend wrapperStyle={{ fontSize: '13px', fontWeight: '600' }} iconType="circle" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )
                        },
                    ].filter((item): item is { key: string; component: React.ReactElement } => Boolean(item));

                    return visibleCharts.length > 0 ? (
                        <section className="mb-8 w-full">
                            <div className={`grid gap-6 w-full ${visibleCharts.length === 1
                                ? 'grid-cols-1'
                                : visibleCharts.length === 2
                                    ? 'grid-cols-1 lg:grid-cols-2'
                                    : 'grid-cols-1 lg:grid-cols-3'
                                }`}>
                                {visibleCharts.map(item => item.component)}
                            </div>
                        </section>
                    ) : null;
                })()}

                {/* Monthly Charts */}
                {hasMonthlyTrendData && (
                    <div className={`grid grid-cols-1 ${hasMonthlyTrendData ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-6 mb-8 ${!hasMonthlyTrendData || monthlyTrend.length === 0 ? 'justify-center' : ''}`}>
                        {/* Monthly Workshop Trend */}
                        <div className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-8 border border-white/20 transition-all duration-500 hover:shadow-blue-500/20 hover:shadow-3xl group">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-[#0D52BD] to-blue-700 bg-clip-text text-transparent">
                                <div className="p-2 bg-gradient-to-br from-[#0D52BD] to-blue-700 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                    <Activity size={22} className="text-white" />
                                </div>
                                Monthly Workshops Conducted
                            </h3>
                            <ResponsiveContainer width="100%" height={350}>
                                <AreaChart data={monthlyTrend} margin={{ top: 20, right: 30, left: -10, bottom: 48 }}>
                                    <defs>
                                        <linearGradient id="colorWorkshops" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0D52BD" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="#0D52BD" stopOpacity={0.1} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 12, fill: '#475569', fontWeight: '600' }}
                                        stroke="#94a3b8"
                                        angle={-40}
                                        textAnchor="end"
                                        height={70}
                                        interval={0}
                                    />
                                    <YAxis tick={{ fontSize: 14, fill: '#475569', fontWeight: '600' }} stroke="#94a3b8" />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="workshops"
                                        stroke="#0D52BD"
                                        strokeWidth={3}
                                        fill="url(#colorWorkshops)"
                                        name="Workshops"
                                        animationDuration={1500}
                                        dot={{ stroke: '#0D52BD', strokeWidth: 2, fill: 'white', r: 5 }}
                                        activeDot={{ r: 8, fill: '#0D52BD', stroke: 'white', strokeWidth: 2 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Monthly Participants Trend */}
                        <div className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-8 border border-white/20 transition-all duration-500 hover:shadow-green-500/20 hover:shadow-3xl group">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                <div className="p-2 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                    <TrendingUp size={22} className="text-white" />
                                </div>
                                Monthly Participants Benefited
                            </h3>
                            <ResponsiveContainer width="100%" height={350}>
                                <AreaChart data={monthlyTrend} margin={{ top: 20, right: 30, left: -10, bottom: 48 }}>
                                    <defs>
                                        <linearGradient id="colorParticipants" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 12, fill: '#475569', fontWeight: '600' }}
                                        stroke="#94a3b8"
                                        angle={-40}
                                        textAnchor="end"
                                        height={70}
                                        interval={0}
                                    />
                                    <YAxis tick={{ fontSize: 14, fill: '#475569', fontWeight: '600' }} stroke="#94a3b8" />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="participants"
                                        stroke="#10b981"
                                        strokeWidth={3}
                                        fill="url(#colorParticipants)"
                                        name="Participants"
                                        animationDuration={1500}
                                        dot={{ stroke: '#10b981', strokeWidth: 2, fill: 'white', r: 5 }}
                                        activeDot={{ r: 8, fill: '#10b981', stroke: 'white', strokeWidth: 2 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Sector Infographic */}
                {hasSectorData && (
                    <section className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-8 border border-white/20 mb-8 transition-all duration-500 hover:shadow-blue-500/20 hover:shadow-3xl group">
                        <h3 className="text-xl font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-[#0D52BD] to-blue-700 bg-clip-text text-transparent">
                            <div className="p-2 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                <Award size={22} className="text-white" />
                            </div>
                            Top Sectors by Participation
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {topSectors.top12.map((sector: any, idx: number) => (
                                <div
                                    key={idx}
                                    className="bg-white rounded-xl p-4 border border-slate-200 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                                >
                                    <div className="flex items-center mb-2">
                                        <span className="text-3xl font-black text-amber-500">#{idx + 1}</span>
                                    </div>
                                    <p className="text-sm font-bold text-[#0D52BD] mt-2">{sector.sector}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* District Performance Table */}
                {appliedFilters.districts.length === 0 && hasDistrictData && (
                    <section className="backdrop-blur-2xl bg-white/70 rounded-3xl shadow-2xl p-8 border border-white/20 transition-all duration-500 hover:shadow-blue-500/20 hover:shadow-3xl group">
                        <h3 className="text-xl font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-[#0D52BD] to-blue-700 bg-clip-text text-transparent">
                            <div className="p-2 bg-gradient-to-br from-[#0D52BD] to-blue-700 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                                <MapPin size={22} className="text-white" />
                            </div>
                            District-wise Performance
                        </h3>

                        <div className="overflow-hidden rounded-2xl border border-white/20">
                            <table className="min-w-full">
                                <thead className="backdrop-blur-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20">
                                    <tr>
                                        <th className="px-5 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">
                                            Rank
                                        </th>
                                        <th className="px-5 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">
                                            District
                                        </th>
                                        <th className="px-5 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">
                                            Workshops
                                        </th>
                                        <th className="px-5 py-4 text-left text-xs font-black text-slate-700 uppercase tracking-wider">
                                            Participants
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {(districtBreakdown.districts || []).map((district: any, idx: number) => (
                                        <tr
                                            key={`${district.district}-${idx}`}
                                            className="backdrop-blur-xl bg-white/40 hover:bg-white/70 transition-all duration-300 group/row"
                                        >
                                            <td className="px-5 py-4 text-sm font-bold text-slate-900">
                                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-xs font-bold">
                                                    {idx + 1}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-sm font-bold text-slate-900 group-hover/row:text-[#0D52BD] transition-colors">
                                                {district.district}
                                            </td>
                                            <td className="px-5 py-4 text-sm font-semibold text-slate-700">{district.workshops}</td>
                                            <td className="px-5 py-4 text-sm font-semibold text-slate-700">
                                                {district.participants.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>

            {/* Custom CSS for animations */}
            <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
        </div>
    );
};

export default RampMEDashboard;


